import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { BindingProps, Message, refProxy, StoreNode } from '../../store';
import { Store } from '../../store/store';
import { JobModel, MomentSelector, SpeakerModel, SubTopic, SyncStatus, Team, } from '../../entities';
import { PlayerState } from '../player/playerState';
import { PlayerFramesetState } from '../playerFrameset/playerFramesetState';
import { SpeakerIdFormState } from '../speakerId/speakerIdFormState';
import { PlayerSpeakerIdController } from './playerSpeakerIdController';

type Props = BindingProps<{
  jobId: string,
  player: PlayerState,
  frameset: PlayerFramesetState,
  playerTutorialHighlightedComponents?: string[];
  teamId?: string | null;
  speakerIdController?: PlayerSpeakerIdController;
}>

export class PlayerSpeakersState
  extends StoreNode {

  private reactionDisposers: (() => void)[] = [];

  readonly scrollElementProxy = refProxy(this);
  readonly sectionProxy = refProxy(this);

  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable(this, {
      isLoading: observable,
      momentsSyncStatus: computed,
      jobId: computed,
      suppressApiQueries: computed,
      job: computed,
      teamId: computed,
      team: computed,
      player: computed,
      frameset: computed,
      speakerIdController: computed,
      momentSelector: computed,
      activeSubtopics: computed,
      activeSpeaker: computed,
      activeSpeakerList: computed,
      mounted: action,
      unmounted: action,
      init: action,
      initEdit: action,
      setLoading: action,
      reset: action,
    });

    this.reactionDisposers.push(
      reaction(() => this.speakerIdForm.isFinished, () => {
        if (this.speakerIdForm.isFinished)
          this.emit('completed')
      })
    );
    this.reactionDisposers.push(
      reaction(() => this.speakerIdForm.currentSpeaker, () => {
        const currentSpeaker = this.speakerIdForm.currentSpeaker;
        if (currentSpeaker) {
          this.speakerIdController.setSelectorSpeaker(currentSpeaker);
          this.player.invoke('pause');
        }

      })
    );
    this.speakerIdForm.listen(this.speakerIdFormListener);
  }

  private speakerIdFormListener = (msg: Message<SpeakerIdFormState>) => {
    const { type, payload } = msg;
    const { player } = this;
    const speaker = payload.speaker;

    switch (type) {
      case 'change':
        const currentActiveTranscript = player.momentView.activeTranscript;
        if (!speaker || currentActiveTranscript?.speakerId === speaker.id)
          return;

        const nextSpeaker = player.momentView.getNextTranscriptBySpeakerId(speaker?.id);
        if (nextSpeaker) {

          if (nextSpeaker.duration > 1) {
            player.invoke('seek', { time: nextSpeaker.startTime + 0.3 }); // just to be sure it's in the frame, and not an overspill of the transcript
          } else {
            player.invoke('jumpToMoment', { moment: nextSpeaker });
          }
        }
        break;
    }

  }

  readonly speakerIdForm: SpeakerIdFormState = new SpeakerIdFormState(this.store, {
    disabledSpeakerSuggestion: () => this.team ? !this.team.isSpeakerSuggestEnabled : false
  })

  isLoading: boolean = false;

  get momentsSyncStatus(): SyncStatus | null {
    return this.job?.momentsSyncStatus || null;
  }

  get jobId(): string | null {
    return this.resolvedProps.jobId ?? null;
  }

  get suppressApiQueries(): boolean {
    return this.resolvedProps.suppressApiQueries;
  }

  get job(): JobModel | null {
    return this.store.maybeGetJob(this.jobId);
  }

  get teamId(): string | null {
    return this.resolvedProps.teamId ?? null;
  }

  get team(): Team | null {
    return this.store.teamManager.getTeam(this.teamId);
  }

  get player(): PlayerState {
    return this.resolvedProps.player;
  }
  get frameset(): PlayerFramesetState | null {
    return this.resolvedProps.frameset ?? null;
  }
  get speakerIdController(): PlayerSpeakerIdController {
    return this.resolvedProps.speakerIdController;
  }


  get momentSelector(): MomentSelector | null {
    return this.player?.selector;
  }

  get activeSubtopics(): SubTopic[] {
    return this.player.momentView.activeSubTopics;
  }

  get activeSpeaker(): SpeakerModel | null {
    return this.player.momentView.activeTopicSpeaker;
  }

  get activeSpeakerList(): SpeakerModel[] {
    return this.player.momentView.displayActiveTopicSpeakerPool;
  }

  async mounted() { /* noop */ }

  unmounted() {
    this.reactionDisposers.forEach(d => d());
    this.reactionDisposers = [];
  }

  async init() {
    this.setLoading(true);
    if (!this.suppressApiQueries) {
      await this.store.apiFetchSpeakers({
        jobId: undefined,
        teamId: this.teamId ?? undefined
      });

      await this.job?.apiBatchFetchSpeakers();
    }

    const { job, team } = this;
    const jobId = job?.id ?? null;
    const teamId = team?.id ?? null;
    if (job) {
      const speakerList = this.player.momentView.activeSpeakerIdSpeakerPool;
      await this.speakerIdForm.init(speakerList, jobId, teamId);
    }
    this.setLoading(false);
  }

  async initEdit(speakerList: SpeakerModel[]) {
    this.setLoading(true);
    if (!this.suppressApiQueries) {
      await this.store.apiFetchSpeakers({
        jobId: undefined,
        teamId: this.teamId ?? undefined
      });

      await this.job?.apiBatchFetchSpeakers();
    }

    const { job, team } = this;
    const jobId = job?.id ?? null;
    const teamId = team?.id ?? null;
    this.speakerIdForm.openEditMode();
    await this.speakerIdForm.init(speakerList, jobId, teamId);
    this.speakerIdForm.speakerBlock.setEditConfirmed();
    this.setLoading(false);
  }

  setLoading(value: boolean) {
    this.isLoading = value;
  }

  reset() {
    this.speakerIdForm.reset();
  }
}